import React, { useEffect } from 'react';
import { Alert, Card, Container, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';

import Option from './option_defaults/option';
import ManagedInput from 'components/form/managed_input';

import PagePicker from './page_picker';
import { setFormData } from './store/actions';
import { saveFormData } from './api';
import PropertyTypePicker from './property_type_picker';
import UserPicker from './user_picker';
import PanelHeader from './shared/panel_header';
import { propertyTypeMappings } from 'helpers/property_types';
import { faBuilding, faFile, faUsers, faWrench } from '@fortawesome/pro-solid-svg-icons';
import DefaultOptionMessage from './shared/default_option_message';
import Header from './shared/header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type DocumentDefaultsType, type DocumentTemplateType } from './types';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

type DocumentTemplateFormProps = {
  afterSavePath: string,
  basePath: string,
  companyId: number,
  formData: DocumentDefaultsType,
  setFormData: (formData: DocumentDefaultsType) => void,
  template: DocumentTemplateType
};

export function DocumentTemplateForm({
  afterSavePath,
  basePath,
  companyId,
  formData,
  template,
  setFormData
}: DocumentTemplateFormProps) {
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  useEffect(() => {
    const onUnload = (event) => {
      if (dirty) event.returnValue = true;
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const handleReturn = () => window.location.href = afterSavePath;

  // can't be called in `saveFormData(...).then` because setDirty changes `dirty` on the *next* render
  if (saved) handleReturn();

  const save = () => {
    const putData: DocumentDefaultsType = { ...formData, companyId: companyId, defaultPages: null };
    if (formData.defaultPages) {
      putData.defaultPages = formData.defaultPages.map(defaultPage => defaultPage.id);
    }
    if (formData.availablePageTemplateTreeIds) {
      putData.availablePageTemplateTreeIds = formData.availablePageTemplateTreeIds.map(page => page.id);
    }

    setSaving(true);
    saveFormData(basePath, putData).then(() => {
      setDirty(false);
      setSaved(true);
    });
  };

  const handleFormUpdate = (newFormData : DocumentDefaultsType) => {
    setDirty(true);
    setFormData(newFormData);
  };

  const updateName = (docName : string) => {
    handleFormUpdate({ ...formData, docName });
  };

  const handleUpdatePropertyType = (
    { propertyTypes, selectAllPropertyTypes } : { propertyTypes: number[], selectAllPropertyTypes: boolean }
  ) => {
    const payload = { ...formData };
    if (selectAllPropertyTypes) {
      payload.selectAllPropertyTypes = true;
      payload.propertyTypes = null;
    } else {
      payload.selectAllPropertyTypes = false;
      if (propertyTypes) {
        payload.propertyTypes = propertyTypes;
      } else {
        payload.propertyTypes = Object.keys(propertyTypeMappings).map(id => parseInt(id));
      }
    }

    handleFormUpdate(payload);
  };

  const handleUpdateOption = (changes) => {
    const docOptions = { ...formData.docOptions, ...changes };

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleRemoveOptions = (names) => {
    const docOptions = { ...formData.docOptions };
    names.forEach((name) => {
      delete docOptions[name];
    });

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleUserChange = ({ userIds, selectAllUserIds }) => {
    const payload = { ...formData };
    if (selectAllUserIds) {
      payload.selectAllUserIds = true;
      payload.userIds = null;
    } else {
      payload.selectAllUserIds = false;
      payload.userIds = userIds;
    }

    handleFormUpdate(payload);
  };

  const options = template.options.map((option, i) => {
    return (
      <div className={i < template.options.length - 1 ? 'mb-3' : ''} key={option.name}>
        <Option
          defaults={formData.docOptions}
          option={option}
          onRemove={handleRemoveOptions}
          onUpdate={handleUpdateOption}
        />
      </div>
    );
  });

  const parentTemplateInfo = (
    <Alert className="mt-4" variant="info">
      <OverlayTrigger
        overlay={
          <Tooltip id="info-link-tooltip">
            {`
              Duplicated documents inherit their look & feel, margins, options,
              and other capabilities from the original document they are duplicated from.
              Click to view parent document.
            `}
          </Tooltip>
        }
        placement="bottom"
      >
        <a
          className="d-inline-flex align-items-center gap-1 text-decoration-none"
          href={template.parentTemplateEditPath}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FontAwesomeIcon className="text-primary" icon={faCircleInfo} size="sm" />
          This document was duplicated. It inherits capabilities from{' '}
          <strong className="fw-semibold">{template.parentTemplateName}</strong>
        </a>
      </OverlayTrigger>
    </Alert>
  );

  return (
    <div>
      <Header
        modelName="Document Template"
        saving={saving}
        templateName={formData.docName || template.name}
        onCancel={handleReturn}
        onSave={save}
      />
      <Container className="mb-8">
        <Card className="p-5">
          <Card.Body>
            <div className="mb-7">
              <h6 className="my-4">Document Settings</h6>
              <p className="fw-semibold my-2">Document Name</p>
              <ManagedInput
                className="form-control w-50"
                name="name-input"
                placeholder={template.name}
                value={formData.docName}
                onChange={change => updateName(change['name-input'])}
              />
              {template.parentTemplateName && parentTemplateInfo}
              <h6 className="mt-7">Default Behavior</h6>
              <p className="my-2">
                Below, you are able to set the default behavior for this document template.
                You can choose which properties it is available for, which doc options it has,
                which users have access to it, and which pages appear by default.
                Any changes you make will be seen when a new version of this document is created.
              </p>
            </div>
            <Card className="mb-7" id="default-doc-property-types">
              <PanelHeader
                icon={faBuilding}
                subtitle="Choose which property types this document is available for"
                title="Property Type"
              />
              <Card.Body>
                <PropertyTypePicker
                  defaultPropertyTypes={template.parameters.propertyTypeIds}
                  selectAllPropertyTypes={formData.selectAllPropertyTypes}
                  selectedPropertyTypes={formData.propertyTypes}
                  onUpdatePropertyType={handleUpdatePropertyType}
                />
              </Card.Body>
            </Card>
            <Card className="mb-7">
              <PanelHeader
                icon={faUsers}
                subtitle="Choose which users are able to create this document"
                title="Users"
              />
              <Card.Body>
                <UserPicker
                  defaultUserIds={template.parameters.userIds}
                  selectAllUserIds={formData.selectAllUserIds}
                  selectedUserIds={formData.userIds}
                  onChange={handleUserChange}
                />
              </Card.Body>
            </Card>
            <div>
              {options.length > 0 && (
                <Card className="mb-7" id="default-doc-options">
                  <PanelHeader
                    icon={faWrench}
                    subtitle="Choose which document options are set by default when this document is created"
                    title="Document Options"
                  />
                  <Card.Body>
                    <DefaultOptionMessage/>
                    {options}
                  </Card.Body>
                </Card>
              )}
            </div>
            <Card className="mb-7">
              <PanelHeader
                icon={faFile}
                subtitle="Choose which pages populate by default in a new version of this document"
                title="Pages"
              />
              <Card.Body>
                <PagePicker
                  defaultPages={formData.defaultPages}
                  templateDefaultPageLibrary={template.defaultPageLibrary}
                  templateDefaultPages={template.defaultPages}
                  userCreated={template.userCreated}
                />
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}


const mapStateToProps = ({ formData }) => ({ formData });
const mapDispatchToProps = { setFormData };

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTemplateForm);
